import { Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/appStore';
import type { UserRole } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import Sidebar from '@/components/Sidebar';
import BottomNav from '@/components/BottomNav';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Menu, Building2, Bell } from 'lucide-react';

const SIDEBAR_WIDTH = 256; // px — keep in sync with the w-64 class

export default function MainLayout() {
  const {
    user,
    currentOrganization,
    isAuthenticated,
    setOrganizations,
    setCurrentOrganization,
    // orgFetched now lives in the Zustand store (not local state) so it
    // survives remounts when the user navigates public → protected routes.
    // Local state reset to false on every remount caused the org spinner
    // to re-flash and navigate() to re-fire after public-route visits.
    orgFetched,
    setOrgFetched,
  } = useAppStore();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Local loading state — only blocks render on the very first org fetch
  // when no org is loaded yet. Never touches global auth isLoading.
  const [orgLoading, setOrgLoading] = useState(false);

  // Fetch user's organisations on mount / user change
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!user || orgFetched) return;
      setOrgLoading(true);

      try {
        const { data, error } = await supabase
          .from('user_organizations')
          .select('*, organization:organizations(*)')
          // Depend on user.id (primitive) not the object reference so
          // Zustand rehydration re-renders don't trigger an extra fetch.
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (error) throw error;

        if (data) {
          const filteredData = data.filter((uo) => {
            if (uo.role !== 'member') return true;
            return (
              uo.organization?.subscription_status === 'active' &&
              uo.organization?.member_portal_enabled === true
            );
          });

          setOrganizations(filteredData);

          if (!currentOrganization && filteredData.length > 0) {
            if (filteredData.length === 1) {
              setCurrentOrganization(filteredData[0].organization, filteredData[0].role as UserRole);
            } else {
              navigate('/organizations');
            }
          } else if (filteredData.length === 0) {
            if (data.length > 0) {
              toast.info('Your organization subscription is inactive. Please contact your administrator.');
            }
            navigate('/organizations/create');
          }
        }
      } catch {
        toast.error('Something went wrong. Please try again.');
      } finally {
        setOrgLoading(false);
        // Write back to the store so remounts don't re-trigger the fetch.
        setOrgFetched(true);
      }
    };

    if (isAuthenticated) fetchOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAuthenticated]);

  // Only block render on the very first org fetch when no org is loaded yet
  if (orgLoading && !currentOrganization) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ── Desktop Sidebar (fixed, always visible) ── */}
      <aside
        className="hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 border-r"
        style={{ width: SIDEBAR_WIDTH }}
      >
        <Sidebar />
      </aside>

      {/* ── Mobile Sidebar (Sheet drawer) ── */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <Sidebar onNavClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* ── Main content area ── */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Sticky header */}
        <header className="sticky top-0 z-30 w-full bg-background/95 backdrop-blur border-b">
          <div className="flex h-14 items-center justify-between px-4">
            {/* Hamburger — mobile only */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Org name — mobile only */}
            <div className="lg:hidden flex items-center gap-2">
              {currentOrganization ? (
                <>
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium text-sm truncate max-w-[160px]">
                    {currentOrganization.name}
                  </span>
                </>
              ) : (
                <span className="font-medium text-sm">Tickally</span>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-24 lg:pb-8 overflow-auto">
          <Outlet />
        </main>

        {/* Bottom nav — mobile only */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-card border-t">
          <BottomNav />
        </div>
      </div>
    </div>
  );
}
